import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { t } from "../../../utils/translate";
import { useState, useEffect, useMemo } from "react";
import ProjectsError from "./components/projects-error";
import ProjectsTable from "./components/projects-table";
import ProjectsHeader from "./components/projects-header";
import MainLayout from "../../components/layout/MainLayout";
import ProjectsLoading from "./components/projects-loading";
import ProjectsPagination from "./components/projects-pagination";
import ProjectsEmptyState from "./components/projects-empty-state";
import {
  getProjectByUserId,
  projectDeleteService,
} from "../../../Services/projectService";

export default function ProjectsPage() {
  const adminId = useSelector((state) => state?.auth?.loggedInUser?._id);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    key: "projectName",
    direction: "ascending",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const getProjectsDataByUserId = async (id) => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const responseData = await getProjectByUserId(id);
      if (responseData) {
        setProjects(responseData?.data);
      } else {
        setProjects([]);
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
      setError("Failed to load projects. Please try again later.");
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminId) {
      getProjectsDataByUserId(adminId);
    }
  }, [adminId]);

  // Filter projects based on search term and status filter
  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      const matchesSearch =
        project.projectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.projectNumber
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        project.companyDetails?.company_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        project.projectDetails?.project_email
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || project.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [projects, searchTerm, statusFilter]);

  // Sort projects
  const sortedProjects = useMemo(() => {
    const sortableProjects = [...filteredProjects];

    return sortableProjects.sort((a, b) => {
      // Handle nested properties
      const getNestedProperty = (obj, path) => {
        const parts = path.split(".");
        return parts.reduce(
          (acc, part) => (acc && acc[part] !== undefined ? acc[part] : ""),
          obj
        );
      };

      let aValue = getNestedProperty(a, sortConfig.key);
      let bValue = getNestedProperty(b, sortConfig.key);

      // Handle string comparison
      if (typeof aValue === "string" && typeof bValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredProjects, sortConfig]);

  // Pagination
  const paginatedProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedProjects.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedProjects, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedProjects.length / itemsPerPage);

  // Request sort
  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Jump to specific page
  const handleJumpToPage = (page) => {
    const pageNumber = Number.parseInt(page, 10);
    if (!isNaN(pageNumber) && pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleDelete = async (id) => {
    const response = await projectDeleteService(id);
    if (response?.data?.status === "success") {
      toast.success(t("deletedSuccessfully"));
      getProjectsDataByUserId(adminId);
    } else {
      toast.error(t("thereIsSomeError"));
    }
  };

  return (
    <MainLayout title="Projects">
      <div className="space-y-6">
        <ProjectsHeader
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
        />

        {loading ? (
          <ProjectsLoading />
        ) : error ? (
          <ProjectsError error={error} />
        ) : projects.length === 0 ? (
          <ProjectsEmptyState />
        ) : (
          <div className="space-y-4">
            <ProjectsTable
              handleDelete={handleDelete}
              projects={paginatedProjects}
              requestSort={requestSort}
              sortConfig={sortConfig}
            />

            <ProjectsPagination
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              handlePageChange={handlePageChange}
              handleJumpToPage={handleJumpToPage}
              totalItems={sortedProjects.length}
            />
          </div>
        )}
      </div>
    </MainLayout>
  );
}
